import React, { useState, useMemo, useRef } from 'react';
import { useAppContext } from '../context/AppContext';
import { replaceVariables, buildVariables, generateBrandedEmail, fileToEmailAttachment } from '../lib/emailService';
import {
  X,
  Send,
  Edit,
  Eye,
  Paperclip,
  FileText,
  Trash2,
  Plus,
  Mail,
  CheckCircle,
  AlertCircle,
  ChevronDown,
} from 'lucide-react';

/**
 * Available PDF attachments library
 */
const ATTACHMENT_LIBRARY = [
  { id: 'function_sheet', name: 'Function Sheet', type: 'auto', description: 'Auto-generated from event details' },
  { id: 'quote_pdf', name: 'Quote / Proposal', type: 'auto', description: 'Auto-generated pricing document' },
  { id: 'terms_conditions', name: 'Terms & Conditions', type: 'static', description: 'Standard T&Cs document' },
  { id: 'menus', name: 'Menu Options', type: 'static', description: 'Current menu selections' },
  { id: 'wedding_brochure', name: 'Wedding Brochure', type: 'static', description: 'Wedding packages & info' },
  { id: 'venue_info', name: 'Venue Information', type: 'static', description: 'Room layouts & capacities' },
];

/**
 * EmailReviewModal - Shows when moving events in the kanban
 * Side-by-side layout: compose on left, live preview on right
 * Lets the user review, edit, attach files, and send or skip the email
 */
export default function EmailReviewModal({
  isOpen,
  onClose,
  event,
  template,
  workflow,
  newStage,
  onSend,
  onSkip,
}) {
  const { venues } = useAppContext();

  // Build the pre-filled email from template
  const venue = venues?.find((v) => v.id === event?.venue_id);
  const variables = useMemo(
    () => (event ? buildVariables(event, venue) : {}),
    [event, venue]
  );

  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [showAttachmentPicker, setShowAttachmentPicker] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [expandAttachments, setExpandAttachments] = useState(true);
  const reviewFileInputRef = useRef(null);

  // Initialize fields when template changes
  React.useEffect(() => {
    if (template && event) {
      // Subject is plain text in email headers — don't HTML-escape.
      // Body is HTML, so default (escaped) substitution is correct.
      setSubject(replaceVariables(template.subject, variables, { escape: false }));
      setBody(replaceVariables(template.body, variables));
      setAttachments([]);
      setUploadedFiles([]);
      setSent(false);
      setSending(false);
      setExpandAttachments(true);
    }
  }, [template, event, variables]);

  if (!isOpen || !event || !template) return null;

  const recipientEmail = event.clientEmail || event.email || 'No email on file';
  const hasEmail = !!(event.clientEmail || event.email);
  const stageName = newStage?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  const handleAddAttachment = (attachment) => {
    if (!attachments.find((a) => a.id === attachment.id)) {
      setAttachments([...attachments, attachment]);
    }
    setShowAttachmentPicker(false);
  };

  const handleRemoveAttachment = (id) => {
    setAttachments(attachments.filter((a) => a.id !== id));
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    const maxSize = 10 * 1024 * 1024;
    const validFiles = files.filter(f => f.size <= maxSize);
    if (validFiles.length < files.length) {
      alert('Some files were too large (max 10MB) and were not added.');
    }
    setUploadedFiles(prev => [...prev, ...validFiles]);
    if (reviewFileInputRef.current) reviewFileInputRef.current.value = '';
  };

  const handleRemoveUploadedFile = (index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSend = async () => {
    setSending(true);
    try {
      // Convert uploaded files to base64 attachments
      const fileAttachments = await Promise.all(
        uploadedFiles.map(file => fileToEmailAttachment(file))
      );

      await onSend({
        to: event.clientEmail || event.email || '',
        subject,
        body,
        attachments: attachments.map((a) => a.id),
        fileAttachments,
        template,
        event,
        workflow,
      });
      setSent(true);
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      setSending(false);
    }
  };

  const handleSkip = () => {
    onSkip({ event, newStage, workflow });
    onClose();
  };

  // HTML preview of branded email with replaced variables
  const previewHtml = generateBrandedEmail(
    replaceVariables(body, variables).replace(/\n/g, '<br>'),
    subject
  );

  // Determine if we're on mobile
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-emerald-50 to-gray-50 rounded-t-xl">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-100 rounded-lg">
              <Mail size={20} className="text-emerald-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Send Email — {event?.name}</h2>
              <p className="text-sm text-gray-600">
                Moving to <span className="font-semibold text-emerald-700">{stageName}</span>
              </p>
            </div>
          </div>
          <button onClick={handleSkip} className="p-2 hover:bg-gray-200 rounded-lg transition">
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Sent success state */}
        {sent ? (
          <div className="flex-1 flex flex-col items-center justify-center py-16">
            <CheckCircle size={48} className="text-emerald-500 mb-4" />
            <h3 className="text-xl font-bold text-gray-900">Email Sent Successfully</h3>
            <p className="text-gray-500 mt-2">
              {template.name} sent to {recipientEmail}
            </p>
          </div>
        ) : (
          <>
            {/* Two-column layout: Compose (left) | Preview (right) */}
            <div className="flex-1 overflow-hidden flex flex-col md:flex-row">
              {/* Left Column - Compose */}
              <div className="w-full md:w-1/2 flex flex-col border-b md:border-b-0 md:border-r border-gray-200 overflow-y-auto">
                <div className="px-5 py-4 space-y-4">
                  {/* Recipient field */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 uppercase mb-2">To</label>
                    <div className={`px-4 py-3 rounded-lg text-sm font-medium ${
                      hasEmail
                        ? 'bg-emerald-50 text-emerald-900 border border-emerald-200'
                        : 'bg-red-50 text-red-700 border border-red-200'
                    }`}>
                      {hasEmail ? (
                        <div>
                          <span className="font-semibold">{event.client}</span>
                          <div className="text-xs text-emerald-700 mt-1">{recipientEmail}</div>
                        </div>
                      ) : (
                        <span className="flex items-center gap-2">
                          <AlertCircle size={14} />
                          No email address on file
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Subject field */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 uppercase mb-2">Subject</label>
                    <input
                      type="text"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      placeholder="Email subject"
                    />
                  </div>

                  {/* Body textarea */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 uppercase mb-2">Message Body</label>
                    <textarea
                      value={body}
                      onChange={(e) => setBody(e.target.value)}
                      rows={8}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none font-mono leading-relaxed"
                      placeholder="Email message with {VARIABLE} placeholders..."
                    />
                    <p className="text-xs text-gray-500 mt-2">Use {'{VARIABLE}'} placeholders like {'{CLIENT_NAME}'}, {'{EVENT_DATE}'}, {'{PACKAGE_TOTAL}'}</p>
                  </div>

                  {/* Attachments section */}
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <button
                      onClick={() => setExpandAttachments(!expandAttachments)}
                      className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition"
                    >
                      <div className="flex items-center gap-2">
                        <Paperclip size={14} className="text-gray-600" />
                        <span className="text-sm font-semibold text-gray-700">
                          Attachments
                          {attachments.length > 0 || uploadedFiles.length > 0 ? (
                            <span className="ml-2 text-xs font-normal text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded">
                              {attachments.length + uploadedFiles.length}
                            </span>
                          ) : null}
                        </span>
                      </div>
                      <ChevronDown size={16} className={`text-gray-500 transition ${expandAttachments ? 'rotate-180' : ''}`} />
                    </button>

                    {expandAttachments && (
                      <div className="px-4 py-4 space-y-3">
                        {/* Library attachments */}
                        {attachments.length > 0 && (
                          <div className="space-y-2">
                            <p className="text-xs font-semibold text-gray-600 uppercase">Selected</p>
                            {attachments.map((att) => (
                              <div
                                key={att.id}
                                className="flex items-center justify-between px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg"
                              >
                                <div className="flex items-center gap-2 flex-1 min-w-0">
                                  <FileText size={14} className="text-emerald-600 flex-shrink-0" />
                                  <div className="min-w-0 flex-1">
                                    <p className="text-sm font-medium text-emerald-900 truncate">{att.name}</p>
                                    {att.type === 'auto' && (
                                      <p className="text-xs text-emerald-700">Auto-generated</p>
                                    )}
                                  </div>
                                </div>
                                <button
                                  onClick={() => handleRemoveAttachment(att.id)}
                                  className="p-1 hover:bg-emerald-100 rounded transition flex-shrink-0"
                                >
                                  <Trash2 size={12} className="text-emerald-600" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Uploaded files */}
                        {uploadedFiles.length > 0 && (
                          <div className="space-y-2">
                            {attachments.length > 0 && <hr className="border-gray-200" />}
                            <p className="text-xs font-semibold text-gray-600 uppercase">Custom Files</p>
                            {uploadedFiles.map((file, idx) => (
                              <div key={idx} className="flex items-center justify-between px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg">
                                <div className="flex items-center gap-2 flex-1 min-w-0">
                                  <Paperclip size={14} className="text-blue-600 flex-shrink-0" />
                                  <div className="min-w-0 flex-1">
                                    <p className="text-sm font-medium text-blue-900 truncate">{file.name}</p>
                                    <p className="text-xs text-blue-700">
                                      {file.size < 1024 * 1024 ? `${(file.size / 1024).toFixed(1)} KB` : `${(file.size / (1024 * 1024)).toFixed(1)} MB`}
                                    </p>
                                  </div>
                                </div>
                                <button
                                  onClick={() => handleRemoveUploadedFile(idx)}
                                  className="p-1 hover:bg-blue-100 rounded transition flex-shrink-0"
                                >
                                  <Trash2 size={12} className="text-blue-600" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Add attachments button */}
                        <button
                          onClick={() => setShowAttachmentPicker(!showAttachmentPicker)}
                          className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100 transition"
                        >
                          <Plus size={14} />
                          Add from Library
                        </button>

                        {/* Attachment picker dropdown */}
                        {showAttachmentPicker && (
                          <div className="bg-white border border-gray-200 rounded-lg p-3 space-y-2 max-h-60 overflow-y-auto">
                            {ATTACHMENT_LIBRARY.filter(
                              (a) => !attachments.find((att) => att.id === a.id)
                            ).map((att) => (
                              <button
                                key={att.id}
                                onClick={() => handleAddAttachment(att)}
                                className="w-full flex items-start gap-2 px-3 py-2 hover:bg-gray-50 rounded-lg transition text-left"
                              >
                                <FileText size={14} className="text-gray-400 flex-shrink-0 mt-0.5" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-gray-900">{att.name}</p>
                                  <p className="text-xs text-gray-500">{att.description}</p>
                                </div>
                              </button>
                            ))}
                            <div className="pt-2 border-t border-gray-200 mt-2">
                              <button
                                onClick={() => { reviewFileInputRef.current?.click(); setShowAttachmentPicker(false); }}
                                className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-50 rounded-lg transition text-left"
                              >
                                <Plus size={14} className="text-emerald-600 flex-shrink-0" />
                                <div>
                                  <p className="text-sm font-medium text-emerald-700">Upload Custom File</p>
                                  <p className="text-xs text-gray-500">PDF, image, doc, etc.</p>
                                </div>
                              </button>
                            </div>
                          </div>
                        )}

                        <input
                          ref={reviewFileInputRef}
                          type="file"
                          multiple
                          onChange={handleFileUpload}
                          className="hidden"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Right Column - Preview */}
              <div className="w-full md:w-1/2 flex flex-col bg-gray-50 border-t md:border-t-0 border-gray-200">
                <div className="px-5 py-4 border-b border-gray-200 bg-white">
                  <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <Eye size={14} className="text-emerald-600" />
                    Live Preview
                  </h3>
                </div>

                <div className="flex-1 overflow-auto p-4">
                  <div className="bg-white rounded-lg border border-gray-300 shadow-sm overflow-hidden">
                    <iframe
                      title="Email Preview"
                      srcDoc={previewHtml}
                      className="w-full h-full border-0 min-h-96"
                      sandbox=""
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Footer actions */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl flex items-center justify-between gap-3">
              <button
                onClick={handleSkip}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition"
              >
                Skip — Don't Send
              </button>
              <button
                onClick={handleSend}
                disabled={!hasEmail || sending}
                className="flex items-center gap-2 px-6 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition"
              >
                {sending ? (
                  <>
                    <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send size={16} />
                    Send Email
                  </>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
